import React, { useCallback, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { MenuList } from '@atlaskit/navigation-system';
import { ButtonMenuItem } from '@atlaskit/navigation-system/side-nav-items/button-menu-item';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup, PopupContent, PopupTrigger } from '@atlaskit/popup/experimental';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';
import { MoreMenuSection } from './more-menu-section';
import type { MoreMenuPopupProps } from './types';

const styles = cssMap({
	popupContentContainerStyles: {
		position: 'relative',
		marginInline: token('space.negative.050'),
	},
	popupContentStyles: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
});

export function MoreMenuPopup({
	productItemsData,
	appItemsData,
	menuItemComponent,
	customizeSidebarMenuItem,
	moreMenuItemLabel,
	onClickMoreMenuTrigger,
	showMoreMenu = true,
	testId,
	triggerRef,
	triggerVisualContentRef,
	dropIndicator,
}: MoreMenuPopupProps): React.JSX.Element {
	const [isOpen, setIsOpen] = useState(false);
	const closeMenu = () => setIsOpen(false);
	const { formatMessage } = useIntl();

	const onClick = useCallback(() => {
		(setIsOpen(!isOpen), onClickMoreMenuTrigger && onClickMoreMenuTrigger());
	}, [onClickMoreMenuTrigger, isOpen]);

	// This ref is ultimately passed to the Modal, which should cause the modal dialog to
	// return focus to the element. This component is "tricky" because it must change the
	// visible-menuitem based on whether the more menu needs to show or not.
	const returnFocusRef = useRef<HTMLElement>(null);

	// This clonedMenuItem allows the customizeSidebarMenuItem to be passed a ref (to allow focussing)
	// and a returnFocusRef, which is passed to the Modal. It doesn't make sense for the caller to
	// define the ref.
	const clonedMenuItem = React.isValidElement(customizeSidebarMenuItem)
		? React.cloneElement(customizeSidebarMenuItem, { returnFocusRef, ref: returnFocusRef })
		: customizeSidebarMenuItem;

	if (!showMoreMenu) {
		return <>{clonedMenuItem}</>;
	}

	return (
		<Popup isOpen={isOpen}>
			<PopupTrigger>
				{(triggerProps) => (
					<ButtonMenuItem
						{...triggerProps}
						elemBefore={<ShowMoreHorizontalIcon label="" color="currentColor" spacing="spacious" />}
						onClick={onClick}
						testId={testId}
						ref={mergeRefs(
							triggerRef
								? [triggerRef, triggerProps.ref, returnFocusRef]
								: [triggerProps.ref, returnFocusRef],
						)}
						visualContentRef={triggerVisualContentRef}
						dropIndicator={dropIndicator}
						isSelected={
							fg('platform-dst-buttonmenuitem-selected-state-support')
								? triggerProps['aria-expanded']
								: undefined
						}
					>
						{moreMenuItemLabel ?? formatMessage(messages.moreMenuTriggerLabel)}
					</ButtonMenuItem>
				)}
			</PopupTrigger>
			{/* Adding listitem role as the parent is a UL */}
			<Box xcss={styles.popupContentContainerStyles} role="listitem">
				<PopupContent
					testId={`${testId}-content`}
					shouldFitContainer
					placement="bottom-start"
					// Offset the top margin of the menu section.
					// This will keep an even spacing of menu items between those inside the dropdown and those above,
					// and keep the icons aligned
					offset={[-4, 4]}
					onClose={() => setIsOpen(false)}
					shouldRenderToParent
					strategy="absolute"
				>
					{() => (
						<Box xcss={styles.popupContentStyles}>
							<MenuList>
								<MoreMenuSection
									itemsData={productItemsData}
									moreMenuItemComponent={menuItemComponent}
									closeMenu={closeMenu}
								/>
								<MoreMenuSection
									itemsData={appItemsData ?? []}
									moreMenuItemComponent={menuItemComponent}
									closeMenu={closeMenu}
								/>
								{clonedMenuItem}
							</MenuList>
						</Box>
					)}
				</PopupContent>
			</Box>
		</Popup>
	);
}
